import _ from 'underscore';

import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {CustomSchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';

import {
  getMoleculeLotUnitTypeFilter,
  MOLECULE_LOT_DISPLAY_NAME,
  MOLECULE_LOT_FIELD_NAMES,
  MOLECULE_LOT_SCHEMA_INTERFACE_ID,
} from './MoleculeLotConstants';

type MoleculeSchemaSummary = NonNullable<SchemaFieldConstants.RequiredLinkTagSchema['tagSchema']>;

/**
 * The target for the molecule link field — either a specific molecule schema or
 * a molecule-type fieldset (custom schema interface).
 */
export type MoleculeLinkTarget =
  | {type: 'schema'; schema: MoleculeSchemaSummary}
  | {type: 'fieldset'; fieldset: CustomSchemaInterfaceSummary};

const MOLECULE_LOT_SYSTEM_NAMES: Set<string> = new Set(
  Object.values(MOLECULE_LOT_FIELD_NAMES).map((f) => f.systemName)
);

function createMoleculeLotField(
  displayName: string,
  systemName: string,
  fieldType: SchemaFieldConstants.FieldType,
  overrides?: Partial<ClientSchemaField>
): ClientSchemaField {
  const base = createClientSchemaField(displayName, systemName);
  return {
    ...base,
    cid: _.uniqueId(),
    fieldType,
    appliedFieldset: {
      id: MOLECULE_LOT_SCHEMA_INTERFACE_ID,
      name: MOLECULE_LOT_DISPLAY_NAME,
    },
    ...overrides,
  };
}

function getMoleculeLinkFieldOverrides(target: MoleculeLinkTarget): Partial<ClientSchemaField> {
  const requiredLink: SchemaFieldConstants.RequiredLink =
    target.type === 'schema'
      ? {tagSchema: target.schema, entitySchemaFieldPath: null, folderItemType: null, schemaInterface: null}
      : {
          tagSchema: null,
          schemaInterface: target.fieldset,
          folderItemType: null,
          entitySchemaFieldPath: null,
        };

  return {
    isRequired: true,
    isParentLink: true,
    appliedFieldset: null,
    requiredLink,
  };
}

function getMoleculeLotEnforcedFields(target: MoleculeLinkTarget): Array<ClientSchemaField> {
  const {MOLECULE_LINK, PURITY, CONCENTRATION_MOLAR, CONCENTRATION_DENSITY} = MOLECULE_LOT_FIELD_NAMES;
  return [
    createMoleculeLotField(
      MOLECULE_LINK.displayName,
      MOLECULE_LINK.systemName,
      SchemaFieldConstants.FieldType.BIOENTITY_LINK,
      getMoleculeLinkFieldOverrides(target)
    ),
    createMoleculeLotField(PURITY.displayName, PURITY.systemName, SchemaFieldConstants.FieldType.FLOAT, {
      appliedFieldset: null,
      simpleRules: [
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldminvalue',
          constantInputs: {float_min_value: 0},
        },
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldmaxvalue',
          constantInputs: {float_max_value: 100},
        },
      ],
    }),
    createMoleculeLotField(
      CONCENTRATION_MOLAR.displayName,
      CONCENTRATION_MOLAR.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
    createMoleculeLotField(
      CONCENTRATION_DENSITY.displayName,
      CONCENTRATION_DENSITY.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
  ];
}

export function removeMoleculeLotEnforcedFields(fields: Array<ClientSchemaField>): Array<ClientSchemaField> {
  return fields.filter(
    (field) =>
      field.appliedFieldset?.id !== MOLECULE_LOT_SCHEMA_INTERFACE_ID &&
      !MOLECULE_LOT_SYSTEM_NAMES.has(field.systemName)
  );
}

export function computeUpdatedFieldsForMoleculeLotWithTarget(
  checked: boolean,
  existingFields: Array<ClientSchemaField>,
  target?: MoleculeLinkTarget
): Array<ClientSchemaField> {
  const fieldsWithoutMoleculeLot = removeMoleculeLotEnforcedFields(existingFields);

  if (!checked) {
    return fieldsWithoutMoleculeLot;
  }

  if (!target) {
    // No target provided — return fields unchanged (caller should ensure a target is selected)
    return fieldsWithoutMoleculeLot;
  }

  return [...getMoleculeLotEnforcedFields(target), ...fieldsWithoutMoleculeLot];
}

export interface DefaultUnitResult {
  unitApiIdentifier: string;
  unitSymbol: string;
}

export interface MoleculeLotDefaultUnits {
  concentrationMolar?: DefaultUnitResult;
  concentrationDensity?: DefaultUnitResult;
}

/**
 * Attempts to resolve default units for molecule lot concentration fields by querying
 * the tenant's available unit types and finding the base unit of the matching dimensional type.
 *
 * Returns the unit identifiers for molar and density fields, or undefined for each if no
 * matching unit type exists on the tenant.
 */
export async function resolveDefaultUnitsForMoleculeLot(): Promise<MoleculeLotDefaultUnits> {
  const result: MoleculeLotDefaultUnits = {};

  const data = await doQuerySafe<UnitTypesQuery>({query: UNIT_TYPES_QUERY}, {});

  const molarFilter = getMoleculeLotUnitTypeFilter(MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.systemName);
  const densityFilter = getMoleculeLotUnitTypeFilter(
    MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.systemName
  );

  for (const unitType of data.unitTypes) {
    if (unitType.archiveData.archived) {
      continue;
    }
    if (molarFilter && molarFilter(unitType) && !result.concentrationMolar) {
      result.concentrationMolar = {
        unitApiIdentifier: unitType.baseUnit.id,
        unitSymbol: unitType.baseUnit.symbol,
      };
    }
    if (densityFilter && densityFilter(unitType) && !result.concentrationDensity) {
      result.concentrationDensity = {
        unitApiIdentifier: unitType.baseUnit.id,
        unitSymbol: unitType.baseUnit.symbol,
      };
    }
  }

  return result;
}

/**
 * Applies default units to molecule lot concentration fields in-place and returns
 * whether any units were successfully applied.
 */
export interface ApplyDefaultUnitsResult {
  updatedFields: Array<ClientSchemaField>;
  /** Which fields had defaults successfully applied. */
  molarApplied: boolean;
  densityApplied: boolean;
}

export function applyDefaultUnitsToFields(
  fields: Array<ClientSchemaField>,
  defaults: MoleculeLotDefaultUnits
): ApplyDefaultUnitsResult {
  let molarApplied = false;
  let densityApplied = false;
  const updatedFields = fields.map((field) => {
    if (
      field.systemName === MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.systemName &&
      defaults.concentrationMolar &&
      !field.unitApiIdentifier
    ) {
      molarApplied = true;
      return {
        ...field,
        unitApiIdentifier: defaults.concentrationMolar.unitApiIdentifier,
        unitSymbol: defaults.concentrationMolar.unitSymbol,
      };
    }
    if (
      field.systemName === MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.systemName &&
      defaults.concentrationDensity &&
      !field.unitApiIdentifier
    ) {
      densityApplied = true;
      return {
        ...field,
        unitApiIdentifier: defaults.concentrationDensity.unitApiIdentifier,
        unitSymbol: defaults.concentrationDensity.unitSymbol,
      };
    }
    return field;
  });

  return {updatedFields, molarApplied, densityApplied};
}
